import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlay, FiCpu, FiBook, FiZap, FiCheckSquare, FiEye, FiEdit2, FiArchive, FiPlusCircle, FiClock, FiTag, FiBarChart2, FiGlobe, FiShare2 } = FiIcons;

const formatIcon = {
  Video: FiPlay,
  Interactive: FiCpu,
  Course: FiBook,
  'Micro-learning': FiZap,
  Quiz: FiCheckSquare
};

// Cleaned up tags to be uniform slate for professional tech look
const tagStyle = 'bg-slate-50 text-slate-600 border-slate-200';

const ModuleCard = ({ module, onPreview, onEdit, onArchive, onAssign, onShare }) => {
  const Icon = formatIcon[module.deliveryFormat] || FiBook;
  const isArchived = module.status === 'Archived';
  const isDraft = module.status === 'Draft';
  const isSpanish = module.language === 'es';

  return (
    <motion.div whileHover={{ y: -2 }} className={`bg-white rounded-xl border shadow-sm overflow-hidden flex flex-col transition-all hover:shadow-md ${isArchived ? 'border-slate-200 opacity-60' : 'border-slate-200'}`}>
      
      {/* Tech Dark Slate Thumbnail */}
      <div className={`h-28 bg-[#0f172a] relative flex items-center justify-center`}>
        <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center backdrop-blur-sm border border-white/5">
          <SafeIcon icon={Icon} className="text-white text-3xl opacity-90" />
        </div>
        <div className="absolute top-3 left-3 flex gap-1.5">
          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold bg-white/10 text-white backdrop-blur-sm border border-white/10`}>
            {module.deliveryFormat}
          </span>
        </div>
        <div className="absolute top-3 right-3 flex gap-1">
          {isSpanish && <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-200">ES</span>}
          {isDraft && <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-200">Draft</span>}
          {isArchived && <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-500 border border-slate-200">Archived</span>}
        </div>
      </div>

      <div className="p-4 flex flex-col flex-1">
        <div className="mb-2 flex items-center gap-1.5 flex-wrap">
          <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${tagStyle}`}>{module.category}</span>
          {isSpanish && (
            <span className="flex items-center gap-0.5 text-xs bg-slate-50 text-slate-600 border border-slate-200 px-1.5 py-0.5 rounded-full font-medium">
              <SafeIcon icon={FiGlobe} className="text-xs" /> Español
            </span>
          )}
        </div>
        
        <h3 className="font-semibold text-slate-800 text-sm leading-snug mb-1">{module.title}</h3>
        <p className="text-xs text-slate-500 leading-relaxed mb-3 flex-1 line-clamp-2">{module.description}</p>
        
        <div className="flex flex-wrap gap-x-3 gap-y-1.5 text-xs text-slate-500 mb-3">
          <span className="flex items-center gap-1"><SafeIcon icon={FiClock} />{module.duration}</span>
          <span className={`px-1.5 py-0.5 rounded border text-xs font-medium ${tagStyle}`}>{module.difficultyLevel}</span>
          <span className="flex items-center gap-1"><SafeIcon icon={FiBarChart2} />{module.usageCount} uses</span>
        </div>

        <div className="flex flex-wrap gap-1 mb-4">
          {module.complianceTags.slice(0, 3).map(tag => (
            <span key={tag} className="flex items-center gap-0.5 text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
              <SafeIcon icon={FiTag} className="text-xs" />{tag}
            </span>
          ))}
        </div>

        <div className="flex gap-1.5 border-t border-slate-100 pt-3">
          <button onClick={() => onPreview(module)} className="flex-1 flex items-center justify-center gap-1 text-xs font-medium text-slate-600 hover:text-blue-600 hover:bg-blue-50 py-1.5 rounded-lg transition-colors"><SafeIcon icon={FiEye} />View</button>
          <button onClick={() => onEdit(module)} className="flex-1 flex items-center justify-center gap-1 text-xs font-medium text-slate-600 hover:text-blue-600 hover:bg-blue-50 py-1.5 rounded-lg transition-colors"><SafeIcon icon={FiEdit2} />Edit</button>
          <button onClick={() => onShare(module)} disabled={!module.id || isDraft} className="flex-1 flex items-center justify-center gap-1 text-xs font-medium text-slate-600 hover:text-blue-600 hover:bg-blue-50 py-1.5 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"><SafeIcon icon={FiShare2} />Share</button>
          <button onClick={() => onAssign(module)} disabled={isArchived || isDraft} className="flex-1 flex items-center justify-center gap-1 text-xs font-medium text-slate-600 hover:text-emerald-600 hover:bg-emerald-50 py-1.5 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"><SafeIcon icon={FiPlusCircle} />Assign</button>
          <button onClick={() => onArchive(module)} className="flex items-center justify-center gap-1 text-xs font-medium text-slate-400 hover:text-red-500 hover:bg-red-50 px-2 py-1.5 rounded-lg transition-colors"><SafeIcon icon={FiArchive} /></button>
        </div>
      </div>
    </motion.div>
  );
};

export default ModuleCard;